import { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Stack,
    Typography,
} from "@mui/material";

import ChartMonthlyOrders from "../components/dashboard/ChartMonthlyOrders";

import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

export default function Dashboard() {

    const apiUrl = process.env.REACT_APP_API_URL;

    const [stats, setStats] = useState({
        totalOrders: 0,
        pendingOrders: 0,
        inTransit: 0,
        arrived: 0,
        shipped: 0,
        totalSales: 0,
        paymentsReceived: 0,
        outstandingBalance: 0,
        totalCustomers: 0,
        inventoryItems: 0,
    });

    const cards = [
        {
            title: "Total Orders",
            value: stats.totalOrders.toLocaleString(),
            icon: <ShoppingBagOutlinedIcon />,
            color: "#2563EB",       // Royal blue
            bg: "#EFF6FF",
        },
        {
            title: "Pending Orders",
            value: stats.pendingOrders.toLocaleString(),
            icon: <PendingActionsOutlinedIcon />,
            color: "#D97706",       // Warm amber
            bg: "#FFFBEB",
        },
        {
            title: "In Transit",
            value: stats.inTransit.toLocaleString(),
            icon: <LocalShippingOutlinedIcon />,
            color: "#C2410C",       // Deep orange — movement, urgency
            bg: "#FFF7ED",
        },
        {
            title: "Arrived",
            value: stats.arrived.toLocaleString(),
            icon: <CheckCircleOutlineOutlinedIcon />,
            color: "#15803D",       // Emerald green — delivered, positive
            bg: "#F0FDF4",
        },
        {
            title: "Shipped",
            value: stats.shipped.toLocaleString(),
            icon: <TaskAltOutlinedIcon />,
            color: "#0369A1",       // Sky blue — in motion, informational
            bg: "#F0F9FF",
        },
        {
            title: "Total Sales",
            value: `₱${stats.totalSales.toLocaleString()}`,
            icon: <PaymentsOutlinedIcon />,
            color: "#0D9488",       // Teal — money, growth
            bg: "#F0FDFA",
        },
        {
            title: "Payments Received",
            value: `₱${stats.paymentsReceived.toLocaleString()}`,
            icon: <AccountBalanceWalletOutlinedIcon />,
            color: "#059669",       // Green — received, confirmed
            bg: "#ECFDF5",
        },
        {
            title: "Outstanding Balance",
            value: `₱${stats.outstandingBalance.toLocaleString()}`,
            icon: <WarningAmberOutlinedIcon />,
            color: "#DC2626",       // Red — needs attention
            bg: "#FEF2F2",
        },
        {
            title: "Total Customers",
            value: stats.totalCustomers.toLocaleString(),
            icon: <GroupsOutlinedIcon />,
            color: "#7C3AED",       // Violet — people, identity
            bg: "#F5F3FF",
        },
        {
            title: "Inventory Items",
            value: stats.inventoryItems.toLocaleString(),
            icon: <Inventory2OutlinedIcon />,
            color: "#475569",       // Slate — neutral, stock
            bg: "#F8FAFC",
        },
    ];

    useEffect(() => {
        const source = new EventSource(
            `${apiUrl}dashboard/dashboard_sse.php`,
            {
                withCredentials: true,
            }
        );

        source.onmessage = (event) => {
            const data = JSON.parse(event.data);
            // console.log(data);
            setStats(data);
        };

        return () => source.close();
    }, []);

    return (
        <Box sx={{ p: 3, minHeight: "100vh" }}>

            {/* ── Page header ── */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0F172A", letterSpacing: -0.4 }}>
                        Dashboard
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#94A3B8", mt: 0.25 }}>
                        Overview of your current metrics
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: 11, color: "#94A3B8", fontFamily: "monospace" }}>
                    Last updated · {new Date().toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}
                </Typography>
            </Stack>

            {/* ── Cards ── */}
            <Grid container spacing={2}>
                {cards.map((card, i) => (
                    <Grid key={card.title}>
                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 1,
                                border: `1px solid ${card.color}22`,   // color + 22 = ~13% opacity
                                // bgcolor: card.bg,
                                color: card.color,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                minWidth: 300,
                                transition: "box-shadow .15s, transform .15s",

                                overflow: "hidden",
                                position: "relative",
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "3px",
                                    minHeight: "3px",
                                    backgroundColor: card.color,
                                },

                                "&:hover": {
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.07)",
                                    transform: "translateY(-2px)",
                                },
                            }}
                        >
                            {/* Top row — icon + title */}
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "10px",
                                        bgcolor: `${card.color}15`,
                                        color: card.color,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    {card.icon}
                                </Box>

                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: "#94A3B8",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.8,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {card.title}
                                </Typography>
                            </Stack>

                            {/* Value — sits below the top row */}
                            <Typography
                                sx={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    color: "#0F172A",
                                    letterSpacing: -0.5,
                                    lineHeight: 1,
                                    fontFamily: "monospace",
                                    mt: 0.5,
                                }}
                            >
                                {card.value}
                            </Typography>

                        </Box>
                    </Grid>
                ))}
            </Grid>

            <ChartMonthlyOrders />

        </Box>
    );
}
